import { getIn } from "@utils/index"
import { richTextToHtml } from "@utils/parseContentfulRichText"
import { getMarketAmount } from "@utils/pricing"
import { emptyRichText } from "@utils/richText"
import { tableRenderers } from "@utils/richTextOptions"
import _ from "lodash-es"
import { useMemo } from "react"

export function useProductMeta({
  avgRating,
  contentfulProduct,
  customerCountryCode = "US",
  goodReview,
  numReviews,
  productData,
}: {
  avgRating: number
  contentfulProduct: any
  customerCountryCode?: string
  goodReview?: any
  numReviews: number
  productData: any
}) {
  const seoTitle = productData?.seoTitle
  const seoDescription = productData?.seoDescription?.seoDescription
  const seoKeywords = productData?.seoKeywords

  const description = useMemo(() => {
    if (seoDescription) return seoDescription
    const options = { renderNode: tableRenderers }
    const htmlString = richTextToHtml(
      productData?.productDescription?.raw ??
        productData?.productDescription ??
        emptyRichText,
      options
    )
    return htmlString.replace(/<[^>]+>/g, " ")
  }, [seoDescription, productData])

  const productMetaData: any = useMemo(() => {
    const firstVariant = getIn(contentfulProduct, "variants.0", {})
    return {
      description,
      images: _.map(
        getIn(contentfulProduct, "variants.0.images", []),
        ({ file }) => file.url
      ),
      keywords: seoKeywords,
      name: getIn(contentfulProduct, "productName"),
      price: getMarketAmount(firstVariant, customerCountryCode, "price"),
      sku: getIn(contentfulProduct, "variants.0.sku"),
      url: getIn(contentfulProduct, "slug"),
      ...(avgRating > 0 && numReviews > 0
        ? {
            ratingValue: avgRating > 4 ? _.toString(avgRating) : undefined,
            reviewCount: _.toString(numReviews),
          }
        : {}),
      ...(goodReview
        ? {
            reviewAuthor: getIn(goodReview, "user.display_name", "Anonymous"),
            reviewBody: goodReview.content,
            reviewRating: _.toString(goodReview.score),
          }
        : {}),
    }
  }, [
    contentfulProduct,
    description,
    seoKeywords,
    avgRating,
    numReviews,
    goodReview,
    customerCountryCode,
  ])

  return { description, productMetaData, seoDescription, seoKeywords, seoTitle }
}
